import { BehaviorSubject, combineLatest, filter, switchMap, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LayoutService } from '@layout/services/layout/layout.service';
import { ClientLeadsRoutes } from '@leads/client-facing/client-facing.routes';
import { DesktopLeadRequestListComponent } from '@leads/client-facing/screens/desktop/desktop-lead-request-list/desktop-lead-request-list.component';
import { MobileLeadRequestListComponent } from '@leads/client-facing/screens/mobile/mobile-lead-request-list/mobile-lead-request-list.component';
import { LeadRequestType, LeadStatus } from '@leads/shared/models/domain/lead-request.domain';
import { ILeadRequest } from '@leads/shared/models/firebase/lead-request.model';
import { LeadRequestService } from '@leads/shared/services/lead-request/lead-request.service';
import { IOrganisationAndOrganisationUser, OrganisationUserAccessLevel } from '@organisations/models/organisation-user';
import { OrganisationSelectedService } from '@organisations/services/organisation-selected/organisation-selected.service';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { PageTitleComponent } from '@shared/components/sections/page-title/page-title.component';
import { UnauthorisedBannerComponent } from '@shared/components/ui/unauthorised-banner/unauthorised-banner.component';

@Component({
	selector: 'app-leads-dashboard',
	imports: [
		CommonModule,
		PageTitleComponent,
		MobileLeadRequestListComponent,
		DesktopLeadRequestListComponent,
		LoadingStateComponent,
		UnauthorisedBannerComponent,
	],
	templateUrl: './leads-dashboard.component.html',
	styleUrl: './leads-dashboard.component.scss',
})
export class LeadsDashboardComponent implements OnInit, OnDestroy {
	selectedOrganisation: IOrganisationAndOrganisationUser | null;
	isLoading = true;
	isSearching = true;
	isMobileSize = true;
	userHasAccess = false;
	leads: Array<ILeadRequest> = [];
	searchTerm$ = new BehaviorSubject<string>('');
	leadStatus$ = new BehaviorSubject<Array<LeadStatus>>([LeadStatus.NEW, LeadStatus.ACTIVE]);
	leadRequestType$ = new BehaviorSubject<LeadRequestType | null>(null);

	destroyed$ = new EventEmitter<void>();

	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private leadRequestService = inject(LeadRequestService);
	private organisationSelectedService = inject(OrganisationSelectedService);
	private layoutService = inject(LayoutService);

	ngOnInit(): void {
		this.layoutService.isMobileSize$.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
			this.isMobileSize = value;
		});

		combineLatest([this.organisationSelectedService.selectedOrganisation, this.searchTerm$, this.leadStatus$, this.leadRequestType$])
			.pipe(
				takeUntil(this.destroyed$),
				filter(([selectedOrganisation]) => !!selectedOrganisation),
				switchMap(([selectedOrganisation, searchTerm, leadStatus, leadRequestType]) => {
					this.selectedOrganisation = selectedOrganisation;
					if (
						selectedOrganisation &&
						[OrganisationUserAccessLevel.OWNER, OrganisationUserAccessLevel.ADMIN].includes(
							selectedOrganisation.organisationUser.accessLevel,
						)
					) {
						this.userHasAccess = true;
						this.isSearching = true;
						return this.leadRequestService.getLeadRequestsByOrganisation(
							selectedOrganisation.organisationUser.organisation,
							searchTerm,
							leadStatus,
							leadRequestType,
						);
					} else {
						this.userHasAccess = false;
						this.isSearching = false;
						return [];
					}
				}),
			)
			.subscribe((leads) => {
				this.leads = leads;
				this.isLoading = false;
				this.isSearching = false;
			});
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	onClick(leadRequest: ILeadRequest) {
		this.router.navigate([ClientLeadsRoutes.ASSESSMENT_REVIEW.replace(':leadRequestId', leadRequest.id as string)], {
			relativeTo: this.route,
		});
	}
}
